import React, { useState, useMemo, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { createOrder } from '../api/api';
import { trackPageView } from '../utils/analytics';

const OrderPage = () => {
    const history = useHistory();
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        fullName: '',
        contact: '',
        solution: '',
        description: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');
    const [errorPopup, setErrorPopup] = useState({ show: false, message: '', type: '' });
    const [successPopup, setSuccessPopup] = useState(false);

    useEffect(() => {
        trackPageView('/order');
    }, []);

    const solutions = [
        { id: 'vitrine', label: 'Site Vitrine', icon: '🌐' },
        { id: 'blog', label: 'Blog', icon: '📝' },
        { id: 'ecommerce', label: 'E-commerce', icon: '🛒' },
        { id: 'sur-mesure', label: 'Sur Mesure', icon: '⚙️' },
        { id: 'ia', label: 'Solution IA', icon: '⭐' },
        { id: 'iot', label: 'Solution IoT', icon: '🤖' },
        { id: 'cybersecurite', label: 'Cybersécurité', icon: '🔒' },
        { id: 'refonte', label: 'Refonte Web', icon: '🎨' }
    ];

    // Questions dynamiques par solution
    const solutionQuestions = {
        vitrine: [
            { key: 'secteur', label: 'Quel est votre secteur d\'activité ?', placeholder: 'Ex: Santé, Commerce, Conseil...', type: 'text' },
            { key: 'pages', label: 'Combien de pages estimez-vous ?', placeholder: 'Ex: 5-8 pages', type: 'text' },
            { key: 'features', label: 'Fonctionnalités souhaitées ?', placeholder: 'Ex: Galerie, Blog, Formulaire de contact...', type: 'textarea' }
        ],
        blog: [
            { key: 'theme', label: 'Thème principal du blog ?', placeholder: 'Ex: Technologie, Lifestyle, Business...', type: 'text' },
            { key: 'frequency', label: 'Fréquence de publication ?', placeholder: 'Ex: 2 articles/mois', type: 'text' },
            { key: 'monetization', label: 'Monétisation prévue ?', placeholder: 'Ex: Publicités, Abonnement, Sponsoring...', type: 'text' }
        ],
        ecommerce: [
            { key: 'products', label: 'Nombre de produits ?', placeholder: 'Ex: 50, 500, 5000+', type: 'text' },
            { key: 'payment', label: 'Modes de paiement souhaités ?', placeholder: 'Ex: Carte bancaire, Orange Money, Wave, Paypal...', type: 'textarea' },
            { key: 'shipping', label: 'Gestion des livraisons ?', placeholder: 'Ex: Livraison locale, International, Retrait en magasin...', type: 'textarea' }
        ],
        ia: [
            { key: 'usecase', label: 'Cas d\'usage principal ?', placeholder: 'Ex: Chatbot, Analyse de données, Prédictions...', type: 'text' },
            { key: 'data', label: 'Volume de données ?', placeholder: 'Ex: Petites données, Millions de lignes...', type: 'text' },
            { key: 'integration', label: 'Systèmes à intégrer ?', placeholder: 'Ex: CRM, ERP, Données existantes...', type: 'textarea' }
        ],
        iot: [
            { key: 'devices', label: 'Appareils IoT concernés ?', placeholder: 'Ex: Capteurs, Caméras, Drones...', type: 'text' },
            { key: 'infrastructure', label: 'Infrastructure existante ?', placeholder: 'Ex: Cloud, On-premises, Hybride...', type: 'text' },
            { key: 'realtime', label: 'Besoin temps réel ?', placeholder: 'Ex: Oui/Non et détails', type: 'text' }
        ],
        cybersecurite: [
            { key: 'infrastructure', label: 'Type d\'infrastructure ?', placeholder: 'Ex: Serveurs web, E-commerce, Infrastructure critique...', type: 'text' },
            { key: 'threats', label: 'Menaces principales identifiées ?', placeholder: 'Ex: Malware, Phishing, DDoS, Données sensibles...', type: 'textarea' },
            { key: 'compliance', label: 'Normes de conformité ?', placeholder: 'Ex: RGPD, PCI-DSS, ISO 27001...', type: 'text' }
        ],
        refonte: [
            { key: 'currentUrl', label: 'URL du site actuel ?', placeholder: 'Ex: https://monsite.com', type: 'text' },
            { key: 'problems', label: 'Problèmes actuels ?', placeholder: 'Ex: Lent, Design obsolète, Pas mobile-friendly...', type: 'textarea' },
            { key: 'deadline', label: 'Deadline idéale ?', placeholder: 'Ex: ASAP, 1 mois, 2 mois...', type: 'text' }
        ],
        'sur-mesure': [
            { key: 'goals', label: 'Objectifs principaux ?', placeholder: 'Ex: Augmenter ventes, Automatiser processus...', type: 'textarea' },
            { key: 'scope', label: 'Étendue du projet ?', placeholder: 'Ex: MVP, Full solution, Integration...', type: 'text' },
            { key: 'budget', label: 'Fourchette budgétaire ?', placeholder: 'Ex: 500k-1M FCFA, 1-2M, Custom...', type: 'text' }
        ]
    };

    // Calculer les étapes totales
    const totalSteps = useMemo(() => {
        if (!formData.solution) return 4; // Nom, Contact, Solution, Description
        const questions = solutionQuestions[formData.solution] || [];
        return 4 + questions.length; // Base + questions spécifiques
    }, [formData.solution]);

    const hints = [
        "Commençons par les bases...",
        "Comment peut-on vous contacter ? 📞",
        "Quelle solution vous intéresse ?",
        "Parlez-nous de votre projet ! 🚀"
    ];

    const footerMessages = [
        "Vous êtes au début de quelque chose de magnifique...",
        "Presque la moitié ! Continuons ensemble 💪",
        "C'est ici que ça devient intéressant...",
        "C'est le moment de vérité ! 🎯"
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Validation du téléphone ou email
    const isValidContact = (contact) => {
        const phoneRegex = /^[+]?[0-9\s\-()]{9,}$/; // Téléphone avec au moins 9 chiffres
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Email valide
        return phoneRegex.test(contact) || emailRegex.test(contact);
    };

    const handleSolutionSelect = (solutionId) => {
        setFormData(prev => ({
            ...prev,
            solution: solutionId
        }));
    };

    const handleNext = () => {
        // Validation simple
        if (currentStep === 1 && !formData.fullName.trim()) {
            setErrorPopup({ show: true, message: 'Veuillez entrer votre nom complet', type: 'error' });
            return;
        }
        if (currentStep === 2 && !formData.contact.trim()) {
            setErrorPopup({ show: true, message: 'Veuillez entrer votre numéro de téléphone ou email', type: 'error' });
            return;
        }
        // Validation du téléphone ou email
        if (currentStep === 2 && !isValidContact(formData.contact)) {
            setErrorPopup({ 
                show: true, 
                message: 'Format invalide. Utilisez un numéro valide (+221...) ou un email', 
                type: 'error' 
            });
            return;
        }
        if (currentStep === 3 && !formData.solution) {
            setErrorPopup({ show: true, message: 'Veuillez sélectionner une solution', type: 'error' });
            return;
        }

        // Valider les questions dynamiques
        if (currentStep > 4) {
            const questions = solutionQuestions[formData.solution] || [];
            const currentQuestion = questions[currentStep - 5];
            if (currentQuestion && !formData[currentQuestion.key]?.trim()) {
                setErrorPopup({ show: true, message: `Veuillez répondre à: "${currentQuestion.label}"`, type: 'error' });
                return;
            }
        }

        if (currentStep === 4 && !formData.description.trim()) {
            setErrorPopup({ show: true, message: 'Veuillez décrire votre projet', type: 'error' });
            return;
        }

        if (currentStep < totalSteps) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            // Créer l'objet données à envoyer
            const dataToSend = {
                fullName: formData.fullName,
                contact: formData.contact,
                solution: formData.solution,
                description: formData.description
            };

            // Ajouter les réponses aux questions spécifiques
            const questions = solutionQuestions[formData.solution] || [];
            questions.forEach(q => {
                if (formData[q.key]) {
                    dataToSend[q.key] = formData[q.key];
                }
            });

            const response = await createOrder(dataToSend);

            if (response) {
                setSuccessPopup(true);
                setTimeout(() => {
                    history.push('/');
                }, 2000);
            }
        } catch (error) {
            console.error('Erreur lors de l\'envoi:', error);
            setErrorPopup({ show: true, message: 'Une erreur est survenue. Veuillez réessayer.', type: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#2f3b52] via-[#3d4e68] to-[#4a5a7d] flex items-center justify-center px-4 py-12">
            {/* Error Popup */}
            {errorPopup.show && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
                    <div className="bg-white rounded-2xl p-8 max-w-sm w-full shadow-2xl animate-fadeInUp">
                        <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-bold text-[#2f3b52] mb-2">Erreur</h3>
                                <p className="text-gray-600 text-sm leading-relaxed">{errorPopup.message}</p>
                            </div>
                        </div>
                        <button
                            onClick={() => setErrorPopup({ show: false, message: '', type: '' })}
                            className="w-full mt-6 px-4 py-3 bg-gradient-to-r from-[#f7876f] to-[#e97058] text-white rounded-lg font-semibold hover:from-[#e97058] hover:to-[#d96049] transition-all duration-200 transform hover:scale-105"
                        >
                            OK
                        </button>
                    </div>
                </div>
            )}

            {/* Success Popup */}
            {successPopup && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
                    <div className="bg-white rounded-2xl p-8 max-w-sm w-full shadow-2xl animate-fadeInUp">
                        <div className="flex flex-col items-center gap-4">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center animate-bounce">
                                <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-[#2f3b52] text-center">Succès !</h3>
                            <p className="text-gray-600 text-sm text-center leading-relaxed">
                                Votre demande a été envoyée avec succès. Notre équipe vous contactera bientôt.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Particles background */}
            <div className="fixed inset-0 pointer-events-none">
                {[...Array(20)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute w-1 h-1 bg-[#f7876f]/30 rounded-full"
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            animation: `float ${5 + Math.random() * 5}s infinite`,
                            animationDelay: `${Math.random() * 5}s`
                        }}
                    />
                ))}
            </div>

            {/* Container */}
            <div className="w-full max-w-2xl relative z-10">
                {/* Progress indicator */}
                <div className="mb-12">
                    <div className="flex justify-between mb-4">
                        {[...Array(Math.min(totalSteps, 4))].map((_, i) => {
                            const step = i + 1;
                            return (
                            <div key={step} className="flex flex-col items-center flex-1">
                                <div
                                    className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg mb-2 transition-all duration-300 ${
                                        step < currentStep
                                            ? 'bg-gradient-to-r from-[#f7876f] to-[#e97058] text-white scale-110'
                                            : step === currentStep
                                            ? 'bg-white text-[#2f3b52] ring-4 ring-[#f7876f]/50 scale-110'
                                            : 'bg-white/30 text-white'
                                    }`}
                                >
                                    {step < currentStep ? '✓' : step}
                                </div>
                                <div className="text-xs text-white/50">
                                    {step === 1 && 'Nom'}
                                    {step === 2 && 'Contact'}
                                    {step === 3 && 'Solution'}
                                    {step === 4 && 'Détails'}
                                </div>
                            </div>
                            );
                        })}
                        {totalSteps > 4 && (
                            <div className="flex flex-col items-center flex-1">
                                <div className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg mb-2 transition-all duration-300 bg-white/30 text-white">
                                    +
                                </div>
                                <div className="text-xs text-white/50">Plus</div>
                            </div>
                        )}
                    </div>
                    <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-[#f7876f] to-[#e97058] transition-all duration-500"
                            style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }}
                        />
                    </div>
                </div>

                {/* Form Container */}
                <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 md:p-12 border border-white/20 shadow-2xl">
                    {/* Step 1: Full Name */}
                    {currentStep === 1 && (
                        <div className="animate-fadeInUp">
                            <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
                                {hints[0]}
                            </h2>
                            <p className="text-[#f7876f] text-lg mb-8">Quel est votre nom complet ? <span className="text-red-400">*</span></p>

                            <input
                                type="text"
                                name="fullName"
                                placeholder="Babacar Khoulé"
                                value={formData.fullName}
                                onChange={handleInputChange}
                                className="w-full px-6 py-4 bg-white/10 border-2 border-white/20 rounded-xl text-white placeholder-white/50 focus:border-[#f7876f] focus:bg-white/20 focus:outline-none transition-all duration-300 text-lg"
                                autoFocus
                            />
                        </div>
                    )}

                    {/* Step 2: Contact */}
                    {currentStep === 2 && (
                        <div className="animate-fadeInUp">
                            <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
                                {hints[1]}
                            </h2>
                            <p className="text-[#f7876f] text-lg mb-8">Numéro de téléphone ou email <span className="text-red-400">*</span></p>

                            <div className="relative">
                                <input
                                    type="text"
                                    name="contact"
                                    placeholder="+221 77 123 45 67 ou email@example.com"
                                    value={formData.contact}
                                    onChange={handleInputChange}
                                    className={`w-full px-6 py-4 bg-white/10 border-2 rounded-xl text-white placeholder-white/50 focus:bg-white/20 focus:outline-none transition-all duration-300 text-lg ${
                                        formData.contact.trim() === ''
                                            ? 'border-white/20 focus:border-[#f7876f]'
                                            : isValidContact(formData.contact)
                                            ? 'border-[#22c55e] focus:border-[#22c55e]'
                                            : 'border-red-500 focus:border-red-500'
                                    }`}
                                    autoFocus
                                />
                                {/* Indicateur de validation */}
                                {formData.contact.trim() !== '' && (
                                    <div className="absolute right-4 top-1/2 -translate-y-1/2">
                                        {isValidContact(formData.contact) ? (
                                            <svg className="w-6 h-6 text-[#22c55e]" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                        ) : (
                                            <svg className="w-6 h-6 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                            </svg>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Message d'aide */}
                            <p className="text-white/60 text-sm mt-4">
                                {formData.contact.trim() === ''
                                    ? 'Entrez un numéro de téléphone (9 chiffres) ou une adresse email'
                                    : isValidContact(formData.contact)
                                    ? '✅ Format valide !'
                                    : '❌ Format invalide. Veuillez vérifier votre entrée'}
                            </p>
                        </div>
                    )}

                    {/* Step 3: Solution Selection */}
                    {currentStep === 3 && (
                        <div className="animate-fadeInUp">
                            <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
                                {hints[2]}
                            </h2>
                            <p className="text-[#f7876f] text-lg mb-8">Quelle solution vous intéresse ? <span className="text-red-400">*</span></p>

                            <div className="grid grid-cols-2 gap-4">
                                {solutions.map(solution => (
                                    <button
                                        key={solution.id}
                                        onClick={() => handleSolutionSelect(solution.id)}
                                        className={`p-4 rounded-xl border-2 transition-all duration-300 transform hover:scale-105 ${
                                            formData.solution === solution.id
                                                ? 'bg-gradient-to-r from-[#f7876f] to-[#e97058] border-[#f7876f] text-white ring-2 ring-[#f7876f] ring-offset-2 ring-offset-[#2f3b52]'
                                                : 'bg-white/10 border-white/20 text-white hover:border-[#f7876f] hover:bg-white/15'
                                        }`}
                                    >
                                        <div className="text-2xl mb-2">{solution.icon}</div>
                                        <div className="text-sm font-semibold">{solution.label}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Step 4: Project Description */}
                    {currentStep === 4 && (
                        <div className="animate-fadeInUp">
                            <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
                                {hints[3]}
                            </h2>
                            <p className="text-[#f7876f] text-lg mb-8">Décrivez votre projet en quelques lignes <span className="text-red-400">*</span></p>

                            <textarea
                                name="description"
                                placeholder="J'aimerais un site qui... avec des fonctionnalités comme..."
                                value={formData.description}
                                onChange={handleInputChange}
                                className="w-full px-6 py-4 bg-white/10 border-2 border-white/20 rounded-xl text-white placeholder-white/50 focus:border-[#f7876f] focus:bg-white/20 focus:outline-none transition-all duration-300 text-base resize-none h-48"
                                autoFocus
                            />
                        </div>
                    )}

                    {/* Dynamic Solution-Specific Questions */}
                    {currentStep > 4 && formData.solution && (() => {
                        const questions = solutionQuestions[formData.solution] || [];
                        const questionIndex = currentStep - 5;
                        const question = questions[questionIndex];

                        if (!question) return null;

                        return (
                            <div className="animate-fadeInUp">
                                <div className="mb-6">
                                    <h3 className="text-lg font-semibold text-white/60">
                                        Question {questionIndex + 1} de {questions.length}
                                    </h3>
                                    <div className="h-1 bg-white/10 rounded-full overflow-hidden mt-2">
                                        <div
                                            className="h-full bg-gradient-to-r from-[#f7876f] to-[#e97058]"
                                            style={{ width: `${((questionIndex + 1) / questions.length) * 100}%` }}
                                        />
                                    </div>
                                </div>

                                <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
                                    {question.label}
                                </h2>
                                <p className="text-[#f7876f] text-lg mb-8">Votre réponse nous aide à mieux comprendre votre projet <span className="text-red-400">*</span></p>

                                {question.type === 'textarea' ? (
                                    <textarea
                                        name={question.key}
                                        placeholder={question.placeholder}
                                        value={formData[question.key] || ''}
                                        onChange={handleInputChange}
                                        className="w-full px-6 py-4 bg-white/10 border-2 border-white/20 rounded-xl text-white placeholder-white/50 focus:border-[#f7876f] focus:bg-white/20 focus:outline-none transition-all duration-300 text-base resize-none h-48"
                                        autoFocus
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        name={question.key}
                                        placeholder={question.placeholder}
                                        value={formData[question.key] || ''}
                                        onChange={handleInputChange}
                                        className="w-full px-6 py-4 bg-white/10 border-2 border-white/20 rounded-xl text-white placeholder-white/50 focus:border-[#f7876f] focus:bg-white/20 focus:outline-none transition-all duration-300 text-lg"
                                        autoFocus
                                    />
                                )}
                            </div>
                        );
                    })()}

                    {/* Footer message */}
                    <div className="mt-12 text-center">
                        <p className="text-white/60 text-sm italic">{footerMessages[currentStep - 1]}</p>
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-4 mt-8">
                        <button
                            onClick={handleBack}
                            disabled={currentStep === 1}
                            className={`flex-1 px-6 py-4 rounded-xl font-bold text-lg transition-all duration-300 ${
                                currentStep === 1
                                    ? 'bg-white/10 text-white/30 cursor-not-allowed'
                                    : 'bg-white/10 text-white border-2 border-white/20 hover:border-[#f7876f] hover:bg-white/20 hover:scale-105'
                            }`}
                        >
                            ← Retour
                        </button>

                        {currentStep < totalSteps ? (
                            <button
                                onClick={handleNext}
                                className="flex-1 px-6 py-4 rounded-xl font-bold text-lg bg-gradient-to-r from-[#f7876f] to-[#e97058] text-white hover:from-[#e97058] hover:to-[#d96049] transform hover:scale-105 transition-all duration-300 shadow-lg"
                            >
                                Suivant →
                            </button>
                        ) : (
                            <button
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                className="flex-1 px-6 py-4 rounded-xl font-bold text-lg bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white hover:from-[#16a34a] hover:to-[#15803d] transform hover:scale-105 transition-all duration-300 shadow-lg disabled:opacity-50"
                            >
                                {isSubmitting ? '⏳ Envoi...' : '✉️ Envoyer'}
                            </button>
                        )}
                    </div>

                    {/* Submit message */}
                    {submitMessage && (
                        <div className="mt-6 text-center">
                            <p className={`text-lg font-semibold ${submitMessage.includes('✅') ? 'text-[#22c55e]' : 'text-red-400'}`}>
                                {submitMessage}
                            </p>
                        </div>
                    )}
                </div>

                {/* Decorative element */}
                <div className="mt-8 text-center">
                    <p className="text-white/40 text-xs">
                        Nous répondons généralement sous 24h ⏱️
                    </p>
                </div>
            </div>

            <style jsx>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .animate-fadeInUp {
                    animation: fadeInUp 0.5s ease-out;
                }
            `}</style>
        </div>
    );
};

export default OrderPage;
